import { __decorate } from "tslib";
import { LitElement, html } from 'lit';
import { property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import infoCircleSvg from '../../assets/icons/info-circle';
import exclamationCircleSvg from '../../assets/icons/exclamation-circle';
import backIcon from '../../assets/icons/back';
import nextIcon from '../../assets/icons/next';
import '../icon';
import '../icon-badge';
import '../typography/tg-label-one';
import '../typography/tg-caption';
import '../spinner';
import '../skeleton';
import styles from './styles';
const VARIANT_TO_ICON_MAP = {
    primary: infoCircleSvg,
    danger: exclamationCircleSvg,
    pure: null,
};
/**
 * @dependency amityeko-icon
 * @dependency amityeko-icon-badge
 * @dependency amityeko-tg-label-one
 *
 * @method show - Show the dialog.
 * @method hide - Hide the dialog.
 * @method setLoading - Sets the loading state.
 *
 * @slot - The content of the dialog.
 *
 * @event amityeko-cancel - Emitted when the "cancel" button is clicked.
 * @event amityeko-back - Emitted when the "back" button is clicked.
 * @event amityeko-confirm - Emitted when the "confirm" button is clicked.
 * @event amityeko-extra-action - Emitted when the "extra" button is clicked.
 * @event amityeko-after-hide - (sl-after-hide) Emitted after the dialog closes and all
 *        animations are complete.
 */
export default class AmityekoDialog extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Variants:
         *   - primary - default dialog view
         *   - danger  - dialog variant that is used mainly as delete or cancel confirmations
         *   - pure    - the fundamental, pure dialog variant, without header and footer,
         *               without any fixed spacing and sizes
         */
        this.variant = 'primary';
        /**
         * This property overrules any value passed to the "label" property and enables Icon title that
         * is predefined per "variant".
         */
        this.hasIconAsLabel = false;
        this.hasBackButton = false;
        this.confirmButtonType = 'button';
        this.hasSuffixIconButton = false;
        this.isFooterInfoTextLoading = false;
        this.isDisabled = false;
        this.isLoading = false;
        this.loading = false;
    }
    show() {
        this.dialog.show();
    }
    hide() {
        this.dialog.hide();
    }
    setLoading(loading) {
        this.loading = loading;
    }
    get shouldShowFooter() {
        if (this.variant === 'pure')
            return false;
        if (this.cancelButtonText || this.confirmButtonText || this.extraActionButtonText) {
            return true;
        }
        return false;
    }
    _onClickCancelButton(e) {
        e.stopPropagation();
        // Note: this approach is implemented as an experiment.
        // Prefer to handle it thru parent's event handling.
        const contentElem = this.contentElements[0];
        if (contentElem === null || contentElem === void 0 ? void 0 : contentElem.handleDialogCancel) {
            contentElem.handleDialogCancel();
        }
        emit(this, 'amityeko-cancel');
        this.hide();
    }
    _onClickConfirmButton(e) {
        e.stopPropagation();
        // Note: this approach is implemented as an experiment.
        // Prefer to handle it thru parent's event handling.
        const contentElem = this.contentElements[0];
        if (contentElem === null || contentElem === void 0 ? void 0 : contentElem.handleDialogConfirm) {
            contentElem.handleDialogConfirm();
        }
        emit(this, 'amityeko-confirm');
    }
    _onClickExtraActionButton(e) {
        e.stopPropagation();
        emit(this, 'amityeko-extra-action');
        this.hide();
    }
    _onAfterHide(e) {
        e.stopPropagation();
        emit(this, 'amityeko-after-hide');
    }
    _renderLabel() {
        if (this.hasIconAsLabel) {
            const iconSvg = VARIANT_TO_ICON_MAP[this.variant];
            if (!iconSvg)
                return null;
            return html `
        <amityeko-icon-badge slot="label" class="label-icon" .containerSize=${'64px'}>
          <amityeko-icon .iconSize=${'36px'}>${iconSvg}</amityeko-icon>
        </amityeko-icon-badge>
      `;
        }
        if (this.label || this.hasBackButton) {
            return html `
        <div slot="label" class="dialog-title">
          ${this.hasBackButton
                ? html `
                <amityeko-button
                  variant="ghost"
                  class="back-icon"
                  ?circle=${true}
                  @click=${this._onRequestBack}
                >
                  <amityeko-icon .iconSize=${'14px'}>${backIcon}</amityeko-icon>
                </amityeko-button>
              `
                : null}
          ${this.label ? html `<amityeko-tg-label-one>${this.label}</amityeko-tg-label-one>` : null}
        </div>
      `;
        }
        return null;
    }
    _renderSuffixIcon() {
        if (this.isLoading) {
            return html `<amityeko-spinner
        .width=${'30px'}
        .height=${'30px'}
        slot="suffix"
      ></amityeko-spinner>`;
        }
        else {
            return html `<amityeko-icon .iconSize=${'16px'} slot="suffix">${nextIcon}</amityeko-icon>`;
        }
    }
    _onRequestClose(e) {
        if (this.loading) {
            e.preventDefault();
        }
        else {
            emit(this, 'amityeko-cancel');
        }
    }
    _onRequestBack(e) {
        emit(this, 'amityeko-back');
    }
    render() {
        return html `
      <sl-dialog
        class=${classMap({
            'with-label-icon': this.hasIconAsLabel,
            'variant--primary': this.variant === 'primary',
            'variant--danger': this.variant === 'danger',
            'variant--pure': this.variant === 'pure',
        })}
        @sl-request-close=${this._onRequestClose}
        @sl-after-hide=${this._onAfterHide}
      >
        ${this._renderLabel()}

        <slot></slot>

        ${this.shouldShowFooter
            ? html `
              <div slot="footer" class="footer">
                <div class="buttons--left">
                  ${this.extraActionButtonText
                ? html `
                        <amityeko-button
                          class="button"
                          variant="ghost"
                          .customColor=${'#fb0404'}
                          ?disabled=${this.loading}
                          @click=${this._onClickExtraActionButton}
                        >
                          ${this.extraActionButtonText}
                        </amityeko-button>
                      `
                : null}
                  ${this.isFooterInfoTextLoading
                ? html `<amityeko-skeleton
                        .width=${'220px'}
                        .height=${'16px'}
                        .borderRadius=${'20px'}
                      ></amityeko-skeleton>`
                : this.footerInfoText
                    ? html `<amityeko-tg-caption>${this.footerInfoText}</amityeko-tg-caption>`
                    : null}
                </div>
                <div class="buttons--right">
                  ${this.cancelButtonText
                ? html `
                        <amityeko-button
                          class="button"
                          variant="ghost"
                          ?disabled=${this.loading}
                          @click=${this._onClickCancelButton}
                        >
                          ${this.cancelButtonText}
                        </amityeko-button>
                      `
                : null}
                  ${this.confirmButtonText && this.confirmButtonType === 'button'
                ? html `
                        <amityeko-button
                          class="button"
                          .variant=${this.variant !== 'pure' ? this.variant : 'primary'}
                          ?loading=${this.loading}
                          ?disabled=${this.isDisabled}
                          @click=${this._onClickConfirmButton}
                          ?hasSuffix=${this.hasSuffixIconButton}
                        >
                          ${this.confirmButtonText}
                          ${this.hasSuffixIconButton
                    ? html `<amityeko-icon slot="suffix">${nextIcon}</amityeko-icon>`
                    : null}
                        </amityeko-button>
                      `
                : null}
                  <!--render confirm button type chip -->
                  ${this.confirmButtonText && this.confirmButtonType === 'chip'
                ? html `
                        <amityeko-chip
                          clickable
                          ?disabled=${this.isDisabled}
                          @click=${this._onClickConfirmButton}
                          selectionColor="#424242"
                        >
                          ${this.confirmButtonChipImage
                    ? html `
                                <img
                                  slot="prefix"
                                  class="chip-image"
                                  src=${this.confirmButtonChipImage}
                                />
                              `
                    : null}
                          <amityeko-tg-caption>${this.confirmButtonText}</amityeko-tg-caption>
                          ${this.hasSuffixIconButton && this._renderSuffixIcon()}
                        </amityeko-chip>
                      `
                : null}
                </div>
              </div>
            `
            : null}
      </sl-dialog>
    `;
    }
}
AmityekoDialog.styles = styles;
__decorate([
    query('sl-dialog')
], AmityekoDialog.prototype, "dialog", void 0);
__decorate([
    queryAssignedElements()
], AmityekoDialog.prototype, "contentElements", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "variant", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "label", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoDialog.prototype, "hasIconAsLabel", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoDialog.prototype, "hasBackButton", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "cancelButtonText", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "confirmButtonText", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "confirmButtonType", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "confirmButtonChipImage", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoDialog.prototype, "hasSuffixIconButton", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "extraActionButtonText", void 0);
__decorate([
    property()
], AmityekoDialog.prototype, "footerInfoText", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoDialog.prototype, "isFooterInfoTextLoading", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoDialog.prototype, "isDisabled", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoDialog.prototype, "isLoading", void 0);
__decorate([
    state()
], AmityekoDialog.prototype, "loading", void 0);
if (!customElements.get('amityeko-dialog')) {
    customElements.define('amityeko-dialog', AmityekoDialog);
}
//# sourceMappingURL=index.js.map